import { Badge, Button, Spinner } from "@aidkitorg/component-library";
import { useCallback, useEffect, useState } from "react";
import { usePost } from "../API";
import { useAsyncJobStatus } from "../Hooks/AsyncJobStatusCheck";
import type { Directory } from '@aidkitorg/aidkit/lib/directory';
import { extractReturnAsJson } from "@aidkitorg/types/lib/endpoints";

type ListResponse = extractReturnAsJson<Directory['/program/admin/support_templates_vector_store/list']>;
type ListSuccess = Extract<ListResponse, { vectorStoreId: string }>;

export default function SupportTemplatesVectorStore() {
  const triggerSync = usePost('/program/admin/support_templates_vector_store/trigger_sync');
  const listFiles = usePost('/program/admin/support_templates_vector_store/list');
  const { phase, setTaskId } = useAsyncJobStatus(3_000);

  const [data, setData] = useState<ListSuccess | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [triggering, setTriggering] = useState(false);

  const fetchList = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const response = await listFiles({});
      if ('error' in response) {
        setData(null);
        setListError(String(response.error));
        return;
      }
      if ('skipped' in response) {
        setData(null);
        setListError('Vector store sync is disabled for this program.');
        return;
      }
      setData(response);
    } catch (e) {
      setListError(e instanceof Error ? e.message : String(e));
    } finally {
      setListLoading(false);
    }
  }, [listFiles]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // Refresh the list once a sync job finishes.
  useEffect(() => {
    if (phase === 'FINISHED' || phase === 'FAILED') {
      fetchList();
    }
  }, [phase, fetchList]);

  const handleSyncClick = useCallback(async () => {
    setTriggering(true);
    setSyncMessage(null);
    try {
      const response = await triggerSync({});
      if (!('error' in response)) {
        setSyncMessage(`Sync started (task ${response.taskId}).`);
        setTaskId(response.taskId);        
      }
    } catch (e) {
      setSyncMessage(`Failed to start sync: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setTriggering(false);
    }
  }, [triggerSync, setTaskId]);

  const isSyncing = phase === 'QUEUED' || phase === 'STARTED';

  return (
    <div className="space-y-6">
      <div>
        <p className="text-gray-700">
          OpenAI vector store containing the latest published version of each support template variant.
          Archived or unpublished templates are excluded.
        </p>
      </div>

      <div className="flex items-center gap-3 w-full">
        <Button onClick={handleSyncClick} isDisabled={triggering || isSyncing || listLoading}>
          {triggering ? 'Starting...' : 'Sync Now'}
        </Button>
        <Button onClick={fetchList} isDisabled={listLoading} variant="secondary">
          Refresh
        </Button>
        {(isSyncing || listLoading) && <div><Spinner size="small" /></div>}
        {(isSyncing || phase === 'FAILED') && <span className="text-sm text-gray-600">Job phase: {phase}</span>}
      </div>

      {phase === 'FAILED' ? (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
          Sync job failed. Check server logs for details.
        </div>
      ) : syncMessage && (
        <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
          {syncMessage}
        </div>
      )}

      {listError && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
          Failed to load vector store: {listError}
        </div>
      )}      

      {
        !!data && (
          <>
            <div className="rounded-md border border-gray-200 bg-white p-4 space-y-1">
              <div><span className="font-medium">Vector store name:</span> <code>{data.vectorStoreName}</code></div>
              <div><span className="font-medium">Vector store ID:</span> <code>{data.vectorStoreId}</code></div>
              <div><span className="font-medium">Total files:</span> {data.files.length}</div>
            </div>
  
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-2">Languages represented</h2>
              {data.langs.length === 0 ? (
                <p className="text-sm text-gray-500">No languages found.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {data.langs.map((lang) => (
                    <Badge key={lang}>{lang}</Badge>
                  ))}
                </div>
              )}
            </div>
  
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-2">Files</h2>
              {data.files.length === 0 ? (
                <p className="text-sm text-gray-500">No files in the vector store yet.</p>
              ) : (
                <div className="overflow-x-auto rounded-md border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Template ID</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Settings ID</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Name</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Lang</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Categories</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Updated</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">File ID</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {data.files.map((f) => (
                        <tr key={f.fileId}>
                          <td className="px-3 py-2">{String(f.attributes.id ?? '')}</td>
                          <td className="px-3 py-2">{String(f.attributes.settings_id ?? '')}</td>
                          <td className="px-3 py-2">{String(f.attributes.name ?? '')}</td>
                          <td className="px-3 py-2">{String(f.attributes.lang ?? '')}</td>
                          <td className="px-3 py-2">{String(f.attributes.categories ?? '')}</td>
                          <td className="px-3 py-2">{String(f.attributes.updated_at ?? '')}</td>
                          <td className="px-3 py-2 font-mono text-xs text-gray-500">{f.fileId}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
    </div>
  );
}
